import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSelecao } from '@/features/feicao/selecao';
import { useI18n } from '@/i18n/I18nProvider';
import { useCamadas } from '@/map/camadas';
import { COR_DA_OPERACAO, COR_DA_SELECAO, estiloPadrao, familiaDaGeometria, } from '@/map/layers';
const LADO_DA_AMOSTRA = 20;
function corComOpacidade(cor, opacidade) {
    const percentual = Math.min(Math.max(opacidade, 0), 1) * 100;
    return `color-mix(in srgb, ${cor} ${percentual}%, transparent)`;
}
function limitar(valor, minimo, maximo) {
    return Math.min(Math.max(valor, minimo), maximo);
}
/** Símbolo derivado da família geométrica e do estilo realmente aplicado à camada. */
function SimboloDaCamada({ estilo }) {
    if (estilo.familia === 'poligono') {
        const forma = {
            width: 18,
            height: 13,
            boxSizing: 'border-box',
            backgroundColor: corComOpacidade(estilo.cor, estilo.opacidadePreenchimento),
            borderStyle: 'solid',
            borderWidth: limitar(estilo.larguraContorno, 1, 3),
            borderColor: corComOpacidade(estilo.corContorno, estilo.opacidadeContorno),
        };
        return _jsx("span", { className: "legenda-cartografica__simbolo", style: forma, "aria-hidden": "true" });
    }
    if (estilo.familia === 'linha') {
        const forma = {
            width: 18,
            height: limitar(estilo.largura, 1, 6),
            backgroundColor: corComOpacidade(estilo.cor, estilo.opacidade),
        };
        return _jsx("span", { className: "legenda-cartografica__simbolo", style: forma, "aria-hidden": "true" });
    }
    const diametro = limitar(estilo.raio * 2, 2, LADO_DA_AMOSTRA);
    const forma = {
        width: diametro,
        height: diametro,
        boxSizing: 'border-box',
        borderRadius: '50%',
        backgroundColor: corComOpacidade(estilo.cor, estilo.opacidade),
        border: `${limitar(estilo.larguraContorno, 0, diametro / 2)}px solid #ffffff`,
        boxShadow: '0 0 0 0.5px rgba(23, 37, 31, 0.35)',
    };
    return _jsx("span", { className: "legenda-cartografica__simbolo", style: forma, "aria-hidden": "true" });
}
function Item({ nome, simbolo }) {
    return (_jsxs("li", { className: "legenda-cartografica__item", title: nome, children: [_jsx("span", { className: "legenda-cartografica__amostra", children: simbolo }), _jsx("span", { className: "legenda-cartografica__nome", children: nome })] }));
}
/**
 * Legenda sobreposta ao mapa: camadas ativas do catálogo (com o estilo atual), a feição
 * selecionada e o resultado de operação. Apenas informativa; deriva dos estados existentes.
 */
export function LegendaCartografica() {
    const { catalogo, ativas, estilos, operacao } = useCamadas();
    const { selecionada } = useSelecao();
    const { t } = useI18n();
    // Ordem visual: `ativas` vai da base ao topo; a legenda lista o topo primeiro.
    const porId = new Map((catalogo ?? []).map((camada) => [camada.id, camada]));
    const camadas = [...ativas].reverse().flatMap((id) => {
        const camada = porId.get(id);
        if (camada === undefined) {
            return [];
        }
        const familia = familiaDaGeometria(camada.tipo_geometria);
        if (familia === null) {
            return [];
        }
        return [{ camada, estilo: estilos[camada.id] ?? estiloPadrao(familia) }];
    });
    const temSelecao = selecionada?.geometria !== null && selecionada?.geometria !== undefined;
    const temOperacao = operacao !== null && operacao.features.length > 0;
    if (camadas.length === 0 && !temSelecao && !temOperacao) {
        return null;
    }
    return (_jsxs("section", { className: "legenda-cartografica", "aria-label": t('legenda.camadasNoMapa'), children: [_jsx("h3", { className: "legenda-cartografica__titulo", children: t('legenda.camadasNoMapa') }), _jsxs("ul", { className: "legenda-cartografica__lista", children: [camadas.map(({ camada, estilo }) => (_jsx(Item, { nome: camada.nome, simbolo: _jsx(SimboloDaCamada, { estilo: estilo }) }, camada.id))), temSelecao ? (_jsx(Item, { nome: t('legenda.feicaoSelecionada'), simbolo: _jsx("span", { className: "legenda-cartografica__simbolo", style: { width: 18, height: 3, backgroundColor: COR_DA_SELECAO }, "aria-hidden": "true" }) })) : null, temOperacao ? (_jsx(Item, { nome: t('legenda.resultadoDeOperacao'), simbolo: _jsx("span", { className: "legenda-cartografica__simbolo", style: { width: 18, height: 0, borderTop: `2px dashed ${COR_DA_OPERACAO}` }, "aria-hidden": "true" }) })) : null] })] }));
}
